// The way down, made to read (cycle 011 playability #1, studio/specs/011-playability-fixes.md). The blind phone and desktop
// testers both stood in reach of the Tavern's trapdoor with PRIMARY reading DOWN and never knew there was one: it was a
// dark 1 m square half under the keg cooler, doors got "ENTER ...", it got nothing, and the minimap showed nothing.
//   - the hatch's own art (the open lid and the dark hole, atlas 'hatch') was there all along, under the cooler: the trapdoor
//     now lies on the storeroom's open floor (data/interiors/tavern.json)
//   - in reach (E's verb is DOWN): a warm line round it pulses, then holds (sorted with the room just over the hatch, as a
//     door's outline is), and a label hangs over it like a door's, "DOWN TO THE CELLAR" (a Hollow floor's hatch: "DOWN A
//     FLOOR"), with a hatch glyph as well as the colour
//   - the minimap: a hatch mark (a brown lid with a pale down chevron), held on the rim when it is off the map's edge
// No iso_render.ts change: everything here is main.ts's own layers (the renderer's sorted world and doorUi-style label layer).
import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { CELL_PX, toScreen, type IsoMap, type Prop } from '../../shared/src/iso';
import { drawText, textWidth, textHeight } from './pixfont';

const WARM = 0xffd27a;

export class HatchMark {
  private g = new Graphics();
  private label: Sprite | null = null; labelText = '';
  /** the hatch drawn now (null: none), and its label sprite: main.ts's tap test */
  shown: Prop | null = null;
  get labelSprite() { return this.label; }
  private shownAt = 0; private reachFor = -1;
  constructor(private labelLayer: Container) { this.g.visible = false; }
  /** the hatches on a map (the street has one, the Tavern's; a Hollow floor its deeper one) */
  static hatches(map: IsoMap): Prop[] { const out: Prop[] = []; for (const p of map.propById.values()) if (p.t === 'hatch' && !p.dead) out.push(p); return out; }
  /** each frame: `sorted` is the active renderer's depth-sorted world; `show` the hatch to draw (null: none); `reach` that
   *  hatch is E's verb now; `hollow` a Hollow floor (the label's words) */
  frame(sorted: Container, p: Prop | null, reach: boolean, hollow: boolean, now: number) {
    this.shown = p;
    // (a Hollow floor's renderer is destroyed with its children when you leave it, and this outline with it: a fresh one -
    // 'reading clear of null' every frame after coming back up from the Cellars, play011 1)
    if (this.g.destroyed) this.g = new Graphics();
    if (this.label?.destroyed) this.label = null;
    if (!p) { this.g.visible = false; if (this.label) this.label.visible = false; this.reachFor = -1; return; }
    if (this.g.parent !== sorted) sorted.addChild(this.g);
    if (reach && this.reachFor !== p.id) { this.reachFor = p.id; this.shownAt = now; } else if (!reach) this.reachFor = -1;
    const C = CELL_PX, x0 = p.x * C, y0 = p.y * C, x1 = (p.x + p.w) * C, y1 = (p.y + p.d) * C, z = 0.05;
    const P = (x: number, y: number) => toScreen(x, y, z);
    const g = this.g; g.clear();
    const quad = (a: number, b: number, c: number, d: number) => [...P(a, b), ...P(c, b), ...P(c, d), ...P(a, d)];
    // in reach: the warm line round it pulses for 1.8 s, then holds (a door's outline)
    if (reach) {
      const t = (now - this.shownAt) / 1000, a = t < 1.8 ? 0.45 + 0.55 * Math.abs(Math.cos(t * Math.PI * 1.7)) : 0.85;
      g.poly(quad(x0 - 0.6, y0 - 0.6, x1 + 0.6, y1 + 0.6)).stroke({ color: WARM, width: 1.5, alpha: a });
    }
    // sorted just over the hatch's own slab (its front corner), as a door's outline is over its door
    g.zIndex = (p.x + p.w + p.y + p.d) * C + 0.6; g.visible = true;
    // the label, as a door's: hangs over it in reach
    const want = hollow ? 'DOWN A FLOOR' : 'DOWN TO THE CELLAR';
    if (reach) {
      if (!this.label || this.labelText !== want) { this.label?.destroy({ texture: true }); this.labelText = want; this.label = makeLabel(want); this.labelLayer.addChild(this.label); }
      if (this.label.parent !== this.labelLayer) this.labelLayer.addChild(this.label);
      const [sx, sy] = toScreen((x0 + x1) / 2, (y0 + y1) / 2, 1.3); this.label.x = Math.round(sx); this.label.y = Math.round(sy);
      this.label.visible = true; this.label.alpha = Math.min(1, 0.5 + (now - this.shownAt) / 400);
    } else if (this.label) this.label.visible = false;
  }
  /** the minimap mark: (cx, cy) your cell, k px per cell, R2 cells each way, W the map's size; off the edge it waits on the rim */
  mini(mg: CanvasRenderingContext2D, map: IsoMap, cx: number, cy: number, k: number, R2: number, W: number) {
    for (const p of HatchMark.hatches(map)) {
      let x = (p.x + p.w / 2 - cx + R2) * k, y = (p.y + p.d / 2 - cy + R2) * k;
      const off = x < 4 || y < 4 || x > W - 4 || y > W - 4;
      if (off && Math.hypot(p.x - cx, p.y - cy) > R2 * 3) continue;   // (a far hatch is no use on the rim)
      x = Math.max(5, Math.min(W - 5, x)); y = Math.max(5, Math.min(W - 5, y));
      // (not a quest mark's filled gold square: a brown lid in a dark frame with a bright down chevron on it)
      mg.fillStyle = '#14101c'; mg.fillRect(x - 5, y - 5, 10, 10);
      mg.fillStyle = '#8a5a2c'; mg.fillRect(x - 4, y - 4, 8, 8);
      mg.fillStyle = '#fff0c8'; mg.beginPath(); mg.moveTo(x - 3, y - 2); mg.lineTo(x + 3, y - 2); mg.lineTo(x, y + 3); mg.fill();   // down
    }
  }
}

/** a label like a door's ("ENTER THE TAVERN"): dark plate, warm rim, a hatch glyph (or a door's), the point down at it */
export function makeLabel(txt: string, glyph: 'hatch' | 'door' = 'hatch'): Sprite {
  const font = 'whyte-bold' as const, pad = 3, ic = 8;
  const tw = textWidth(txt, 1, font), th = textHeight(1, font) - 2;
  const cv = document.createElement('canvas'); cv.width = tw + pad * 3 + ic + 1; cv.height = th + pad * 2 + 3;
  const g = cv.getContext('2d')!, bh = cv.height - 3;
  g.fillStyle = '#0f0e22'; g.fillRect(0, 0, cv.width, bh);
  g.strokeStyle = '#ffd27a'; g.lineWidth = 1; g.strokeRect(0.5, 0.5, cv.width - 1, bh - 1);
  // the glyph: a lid seen from above, its frame, two seams and a down chevron under it
  const gy = Math.max(1, Math.round((bh - 9) / 2));
  if (glyph === 'door') {   // (iso_render's door glyph: a frame round a lit leaf)
    g.fillStyle = '#ffd27a'; g.fillRect(pad, gy, 7, 9); g.fillStyle = '#0f0e22'; g.fillRect(pad + 1, gy + 1, 5, 8); g.fillStyle = '#f0b65e'; g.fillRect(pad + 2, gy + 2, 3, 7);
  } else {
  g.fillStyle = '#ffd27a'; g.fillRect(pad, gy, ic, 6); g.fillStyle = '#0f0e22'; g.fillRect(pad + 1, gy + 1, ic - 2, 4);
  g.fillStyle = '#f0b65e'; g.fillRect(pad + 2, gy + 2, ic - 4, 2); g.fillStyle = '#0f0e22'; g.fillRect(pad + 4, gy + 1, 1, 4);
  g.fillStyle = '#ffd27a'; g.beginPath(); g.moveTo(pad + 1, gy + 7); g.lineTo(pad + ic - 1, gy + 7); g.lineTo(pad + ic / 2, gy + 10); g.fill();
  }
  drawText(g, txt, pad * 2 + ic, pad, '#ffd27a', 1, '#000000', font);
  const m = cv.width >> 1; g.fillStyle = '#0f0e22'; g.beginPath(); g.moveTo(m - 3, bh); g.lineTo(m + 3, bh); g.lineTo(m, bh + 3); g.fill();
  const spr = new Sprite(Texture.from(cv)); spr.anchor.set(0.5, 1); spr.visible = false;
  return spr;
}
